import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, FileText, CheckCircle, XCircle, Loader, Clock, Trash2, ChevronLeft, ChevronRight, Search, AlertCircle } from 'lucide-react';
import { formatDate } from '../utils/formatDate';
import { getRuntimeColor } from '../utils/runtimeFormatter';
import './AdminPages.css';

const BatchDetail = () => {
  const { batchId } = useParams();
  const navigate = useNavigate();
  const [batch, setBatch] = useState(null);
  const [workOrders, setWorkOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [pagination, setPagination] = useState({ total: 0, skip: 0, limit: 25, page: 1, pages: 1 });
  const [totalRuntime, setTotalRuntime] = useState(0);
  const pollRef = useRef(null);

  // Fetch batch status
  const fetchBatchStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const resp = await fetch(`/api/batch/${batchId}/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (resp.ok) {
        const data = await resp.json();
        setBatch(data);
        // Auto-refresh while processing
        if (data.status === 'PROCESSING' && !pollRef.current) {
          startPolling();
        } else if (data.status !== 'PROCESSING' && pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      }
    } catch (err) {
      console.error('Failed to fetch batch status:', err);
    }
  };

  const startPolling = () => {
    if (pollRef.current) return;
    pollRef.current = setInterval(() => {
      fetchBatchStatus();
      fetchWorkOrders(0);
    }, 2000);
  };

  const formatWorkOrderRuntime = (durationSeconds) => {
    if (!durationSeconds || durationSeconds <= 0) return { display: '-', color: '#a0aec0', bg: 'transparent' };

    const totalSeconds = Math.round(durationSeconds);
    const seconds = totalSeconds % 60;
    const minutes = Math.floor(totalSeconds / 60) % 60;
    const hours = Math.floor(totalSeconds / 3600);
    const totalMinutes = totalSeconds / 60;

    let display = '';
    if (hours > 0) {
      display = `${hours}h ${minutes}m ${seconds}s`;
    } else if (minutes > 0) {
      display = `${minutes}m ${seconds}s`;
    } else {
      display = `${seconds}s`;
    }

    // Color coding based on total minutes
    let color = '#a0aec0';
    let bg = 'transparent';

    if (totalMinutes >= 5) {
      color = '#991B1B';
      bg = '#FEE2E2';
    } else if (totalMinutes >= 2.5) {
      color = '#B45309';
      bg = '#FEF3C7';
    } else if (totalMinutes > 0) {
      color = '#166534';
      bg = '#DCFCE7';
    }

    return { display, color, bg };
  };

  useEffect(() => {
    fetchBatchStatus();
    fetchWorkOrders(0);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [batchId]);

  const fetchWorkOrders = async (skip = 0, searchTerm = search) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({ skip, limit: 25 });
      if (searchTerm) params.append('search', searchTerm);

      const resp = await fetch(`/api/batch/${batchId}/work-orders?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (resp.ok) {
        const data = await resp.json();
        setWorkOrders(data.data || []);
        setPagination(data.pagination || { total: 0, skip: 0, limit: 25, page: 1, pages: 1 });
        setTotalRuntime(data.totalRuntimeMin || 0);
      } else {
        setError('Failed to load batch work orders');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    const term = e.target.value;
    setSearch(term);
    clearTimeout(window._batchDetailSearch);
    window._batchDetailSearch = setTimeout(() => fetchWorkOrders(0, term), 500);
  };

  const downloadReport = () => {
    const token = localStorage.getItem('token');
    window.open(`/api/batch/${batchId}/report?token=${token}`, '_blank');
  };

  const deleteBatch = async () => {
    if (!window.confirm(`Delete batch ${batchId} and ALL its work orders? This cannot be undone.`)) return;
    const token = localStorage.getItem('token');
    const resp = await fetch(`/api/batch/${batchId}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (resp.ok) navigate('/batch-extract');
    else alert('Delete failed');
  };

  const completed = workOrders.filter(wo => wo.status === 'completed').length;
  const failed = workOrders.filter(wo => wo.status === 'failed' || wo.status === 'cancelled').length;
  const processing = workOrders.filter(wo => wo.status === 'processing').length;

  const getStatusBadge = (status) => {
    const styles = {
      completed: { bg: '#C6F6D5', color: '#22543D' },
      processing: { bg: '#FEFCBF', color: '#744210' },
      failed: { bg: '#FED7D7', color: '#742A2A' },
      cancelled: { bg: '#FED7D7', color: '#742A2A' },
      created: { bg: '#E2E8F0', color: '#4A5568' }
    };
    const s = styles[status] || styles.created;
    return (
      <span style={{ padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600, background: s.bg, color: s.color }}>
        {status}
      </span>
    );
  };

  return (
    <div className="admin-page">
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%' }}>
          <button onClick={() => navigate('/batch-extract')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, flexShrink: 0 }}>
            <ArrowLeft size={20} />
          </button>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
              <h2 style={{ margin: 0 }}>Batch: <code style={{ fontSize: 16 }}>{batchId}</code></h2>
              {batch && (
                <span style={{
                  padding: '4px 12px',
                  borderRadius: 12,
                  fontSize: 12,
                  fontWeight: 600,
                  background: batch.status === 'PROCESSING' ? '#FEFCBF' : batch.status === 'COMPLETED' ? '#C6F6D5' : '#FED7D7',
                  color: batch.status === 'PROCESSING' ? '#744210' : batch.status === 'COMPLETED' ? '#22543D' : '#742A2A'
                }}>
                  {batch.status === 'PROCESSING' && '🟡 PROCESSING'}
                  {batch.status === 'COMPLETED' && '🟢 COMPLETED'}
                  {batch.status === 'FAILED' && '🔴 FAILED'}
                </span>
              )}
            </div>
            <p style={{ margin: 0, color: '#718096', fontSize: 13 }}>
              {pagination.total} work orders | {completed} completed | {failed} failed | {processing} processing
              {totalRuntime > 0 && (() => {
                const colors = getRuntimeColor({ totalMinutes: totalRuntime });
                return (
                  <span style={{
                    marginLeft: 8,
                    padding: '2px 8px',
                    borderRadius: 4,
                    background: colors.bg,
                    color: colors.color,
                    fontWeight: 600
                  }}>
                    ⏱️ Runtime: {Math.round(totalRuntime * 10) / 10} min
                  </span>
                );
              })()}
            </p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button onClick={downloadReport} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: '#3182ce', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>
            <Download size={14} /> Excel Report
          </button>
          <button onClick={deleteBatch} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: '#e53e3e', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>
            <Trash2 size={14} /> Delete
          </button>
        </div>
      </div>

      {/* Batch Progress Section */}
      {batch && (
        <div style={{
          marginBottom: 24,
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
          borderRadius: 12,
          padding: 24,
          boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
          border: '1px solid rgba(255,255,255,0.5)'
        }}>
          {/* Status & Info Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
            <div>
              <h3 style={{ margin: '0 0 8px 0', fontSize: 18, fontWeight: 700, color: '#1a202c' }}>Processing Progress</h3>
              {batch.status === 'PROCESSING' && (
                <p style={{ margin: 0, fontSize: 13, color: '#4a5568', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span className="spin" style={{ display: 'inline-block', fontSize: 12 }}>●</span>
                  Currently processing: <strong>{batch.currentFile || 'Preparing...'}</strong>
                </p>
              )}
              {batch.status === 'COMPLETED' && (
                <p style={{ margin: 0, fontSize: 13, color: '#22543d', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <CheckCircle size={14} style={{ color: '#38a169' }} />
                  Completed on {batch.completedAt ? new Date(batch.completedAt).toLocaleString() : 'recently'}
                </p>
              )}
            </div>
            <div style={{
              padding: '8px 16px',
              borderRadius: 20,
              fontSize: 12,
              fontWeight: 700,
              background: batch.status === 'PROCESSING' ? '#FEF3C7' : batch.status === 'COMPLETED' ? '#DCFCE7' : '#FECACA',
              color: batch.status === 'PROCESSING' ? '#B45309' : batch.status === 'COMPLETED' ? '#166534' : '#991B1B'
            }}>
              {batch.status === 'PROCESSING' && '🟡 PROCESSING'}
              {batch.status === 'COMPLETED' && '✅ COMPLETED'}
              {batch.status === 'FAILED' && '❌ FAILED'}
            </div>
          </div>

          {/* Metrics Grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 16, marginBottom: 24 }}>
            {/* Total PDFs */}
            <div style={{
              background: 'white',
              padding: 16,
              borderRadius: 10,
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              border: '1px solid #e2e8f0',
              textAlign: 'center',
              transition: 'transform 0.2s, box-shadow 0.2s',
              cursor: 'default'
            }} onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.12)'; }} onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)'; }}>
              <p style={{ margin: '0 0 8px 0', fontSize: 12, color: '#718096', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>📄 Total PDFs</p>
              <p style={{ margin: 0, fontSize: 32, fontWeight: 800, color: '#2d3748' }}>{batch.totalPdfs || 0}</p>
            </div>

            {/* Completed */}
            <div style={{
              background: 'white',
              padding: 16,
              borderRadius: 10,
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              border: '1px solid #c6f6d5',
              textAlign: 'center',
              transition: 'transform 0.2s, box-shadow 0.2s',
              cursor: 'default'
            }} onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.12)'; }} onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)'; }}>
              <p style={{ margin: '0 0 8px 0', fontSize: 12, color: '#22543d', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>✅ Completed</p>
              <p style={{ margin: 0, fontSize: 32, fontWeight: 800, color: '#38a169' }}>{batch.completed || 0}</p>
            </div>

            {/* Failed */}
            <div style={{
              background: 'white',
              padding: 16,
              borderRadius: 10,
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              border: '1px solid #fed7d7',
              textAlign: 'center',
              transition: 'transform 0.2s, box-shadow 0.2s',
              cursor: 'default'
            }} onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.12)'; }} onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)'; }}>
              <p style={{ margin: '0 0 8px 0', fontSize: 12, color: '#742a2a', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>❌ Failed</p>
              <p style={{ margin: 0, fontSize: 32, fontWeight: 800, color: '#e53e3e' }}>{batch.failed || 0}</p>
            </div>

            {/* Remaining */}
            <div style={{
              background: 'white',
              padding: 16,
              borderRadius: 10,
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              border: '1px solid #fef3c7',
              textAlign: 'center',
              transition: 'transform 0.2s, box-shadow 0.2s',
              cursor: 'default'
            }} onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.12)'; }} onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)'; }}>
              <p style={{ margin: '0 0 8px 0', fontSize: 12, color: '#b45309', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>⏳ Remaining</p>
              <p style={{ margin: 0, fontSize: 32, fontWeight: 800, color: '#d69e2e' }}>{(batch.totalPdfs || 0) - (batch.completed || 0) - (batch.failed || 0)}</p>
            </div>
          </div>

          {/* Progress Bar */}
          {batch.totalPdfs > 0 && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: '#2d3748' }}>Overall Progress</span>
                <span style={{ fontSize: 16, fontWeight: 800, color: batch.status === 'COMPLETED' ? '#38a169' : '#3182ce' }}>
                  {Math.round(((batch.completed || 0) + (batch.failed || 0)) / (batch.totalPdfs || 1) * 100)}%
                </span>
              </div>
              <div style={{
                background: 'rgba(255,255,255,0.3)',
                borderRadius: 12,
                height: 32,
                overflow: 'hidden',
                boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.06)'
              }}>
                <div style={{
                  background: batch.status === 'COMPLETED' && batch.failed === 0
                    ? 'linear-gradient(90deg, #10b981 0%, #059669 100%)'
                    : batch.status === 'FAILED'
                    ? 'linear-gradient(90deg, #ef4444 0%, #dc2626 100%)'
                    : 'linear-gradient(90deg, #3b82f6 0%, #2563eb 100%)',
                  height: '100%',
                  width: `${Math.round(((batch.completed || 0) + (batch.failed || 0)) / (batch.totalPdfs || 1) * 100)}%`,
                  transition: 'width 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  paddingRight: 12,
                  position: 'relative',
                  boxShadow: '0 0 20px rgba(59, 130, 246, 0.4)'
                }} />
              </div>
            </div>
          )}

          {/* Current File Status */}
          {batch.status === 'PROCESSING' && (
            <div style={{
              background: 'white',
              padding: 14,
              borderRadius: 8,
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              fontSize: 13,
              border: '1px solid #fef3c7',
              animation: 'pulse 2s infinite'
            }}>
              <Loader size={16} className="spin" style={{ color: '#d69e2e', flexShrink: 0 }} />
              <div>
                <span style={{ color: '#4a5568' }}>Currently processing:</span>
                <p style={{ margin: '4px 0 0 0', fontWeight: 600, color: '#2d3748', wordBreak: 'break-word' }}>
                  📄 {batch.currentFile}
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Search */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ position: 'relative', width: 320 }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: 9, color: '#a0aec0' }} />
          <input
            type="text"
            placeholder="Search by Work Order ID or File Name..."
            value={search}
            onChange={handleSearch}
            style={{ width: '100%', padding: '8px 8px 8px 30px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13 }}
          />
        </div>
      </div>

      {/* Table */}
      <div className="admin-card" style={{ padding: 0 }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}><Loader size={20} className="spin" /> Loading...</div>
        ) : error ? (
          <div style={{ color: '#e53e3e', padding: 20 }}>{error}</div>
        ) : (
          <>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f7fafc' }}>
                  <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #e2e8f0', width: 40 }}>#</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>Work Order ID</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>File Name</th>
                  <th style={{ padding: '10px 12px', textAlign: 'center', borderBottom: '1px solid #e2e8f0' }}>Status</th>
                  <th style={{ padding: '10px 12px', textAlign: 'center', borderBottom: '1px solid #e2e8f0' }}>Progress</th>
                  <th style={{ padding: '10px 12px', textAlign: 'center', borderBottom: '1px solid #e2e8f0' }}>⏱️ Runtime</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>Created At</th>
                  <th style={{ padding: '10px 12px', textAlign: 'center', borderBottom: '1px solid #e2e8f0' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {workOrders.length === 0 ? (
                  <tr><td colSpan="8" style={{ textAlign: 'center', padding: 30, color: '#a0aec0' }}>No work orders found</td></tr>
                ) : workOrders.map((wo, idx) => (
                  <tr key={wo._id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '10px 12px' }}>{pagination.skip + idx + 1}</td>
                    <td style={{ padding: '10px 12px' }}><code style={{ fontSize: 11 }}>{wo._id}</code></td>
                    <td style={{ padding: '10px 12px' }}><FileText size={12} style={{ marginRight: 4 }} />{wo.customerName || '-'}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'center' }}>{getStatusBadge(wo.status)}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'center' }}>{wo.fileProgress || 0}%</td>
                    <td style={{
                      padding: '10px 12px',
                      textAlign: 'center',
                      fontWeight: 600,
                      color: formatWorkOrderRuntime(wo.duration).color,
                      background: formatWorkOrderRuntime(wo.duration).bg,
                      borderRadius: '4px'
                    }}>
                      {formatWorkOrderRuntime(wo.duration).display}
                    </td>
                    <td style={{ padding: '10px 12px', fontSize: 12, color: '#718096' }}>{formatDate(wo.createdAt)}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                      <button
                        onClick={() => navigate(`/work-order/${wo._id}`)}
                        style={{ background: '#EBF8FF', border: 'none', color: '#3182ce', cursor: 'pointer', fontSize: 12, padding: '4px 10px', borderRadius: 4, fontWeight: 500 }}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderTop: '1px solid #e2e8f0' }}>
                <span style={{ fontSize: 12, color: '#718096' }}>
                  Showing {pagination.skip + 1}-{Math.min(pagination.skip + pagination.limit, pagination.total)} of {pagination.total}
                </span>
                <div style={{ display: 'flex', gap: 4 }}>
                  <button
                    onClick={() => fetchWorkOrders(Math.max(0, pagination.skip - pagination.limit))}
                    disabled={pagination.page <= 1}
                    style={{ padding: '4px 8px', border: '1px solid #e2e8f0', borderRadius: 4, cursor: pagination.page <= 1 ? 'not-allowed' : 'pointer', background: 'white', opacity: pagination.page <= 1 ? 0.5 : 1 }}
                  >
                    <ChevronLeft size={14} />
                  </button>
                  <span style={{ padding: '4px 12px', fontSize: 12, lineHeight: '22px' }}>Page {pagination.page} of {pagination.pages}</span>
                  <button
                    onClick={() => fetchWorkOrders(pagination.skip + pagination.limit)}
                    disabled={pagination.page >= pagination.pages}
                    style={{ padding: '4px 8px', border: '1px solid #e2e8f0', borderRadius: 4, cursor: pagination.page >= pagination.pages ? 'not-allowed' : 'pointer', background: 'white', opacity: pagination.page >= pagination.pages ? 0.5 : 1 }}
                  >
                    <ChevronRight size={14} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default BatchDetail;
